import { Link, useParams } from "@tanstack/react-router";
import { AdminShell } from "@project/admin/components/admin-shell";
import { SectionCard } from "@project/admin/components/module-ui";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  ArrowLeft,
  Phone,
  HeartPulse,
  BedDouble,
  Calendar,
  Droplet,
} from "lucide-react";
import {
  mockResidents,
  mockResidentActivities,
} from "@project/lib/mock-modules";

export default function ResidentDetail() {
  const { id } = useParams({
    from: "/admin/_modules/_elder-care/residents/$id",
  });
  const r = mockResidents.find(x => x.id === id);

  if (!r) {
    return (
      <AdminShell title="Resident not found">
        <p className="text-sm text-muted-foreground">
          We couldn&apos;t find this resident.
        </p>
        <Button asChild variant="outline" size="sm" className="mt-3">
          <Link to="/admin/elder-care/residents">
            <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back to directory
          </Link>
        </Button>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title={r.name}
      subtitle={`Resident ${r.id} · Room ${r.room}`}
      actions={
        <Button asChild size="sm" variant="outline">
          <Link to="/admin/elder-care/residents">
            <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Directory
          </Link>
        </Button>
      }
    >
      <div className="grid gap-4 lg:grid-cols-3">
        <SectionCard
          title="Profile"
          subtitle="Personal & admission details"
          className="lg:col-span-2"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Info
              icon={<BedDouble className="h-4 w-4" />}
              label="Room"
              value={r.room}
            />
            <Info
              icon={<HeartPulse className="h-4 w-4" />}
              label="Health Status"
              value={<Badge variant="secondary">{r.health}</Badge>}
            />
            <Info
              icon={<Droplet className="h-4 w-4" />}
              label="Blood Group"
              value={r.bloodGroup}
            />
            <Info
              icon={<Calendar className="h-4 w-4" />}
              label="Admitted On"
              value={new Date(r.admittedOn).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            />
            <Info label="Age / Gender" value={`${r.age} years · ${r.gender}`} />
            <Info label="Status" value={<Badge>{r.status}</Badge>} />
          </div>
          <div className="mt-5">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Medical Conditions
            </div>
            <div className="flex flex-wrap gap-1.5">
              {r.conditions.length === 0 ? (
                <span className="text-sm text-muted-foreground">
                  None on file
                </span>
              ) : (
                r.conditions.map((c: string) => (
                  <Badge key={c} variant="outline">
                    {c}
                  </Badge>
                ))
              )}
            </div>
          </div>
          <div className="mt-5">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Care Notes
            </div>
            <p className="text-sm text-foreground">{r.notes}</p>
          </div>
        </SectionCard>

        <SectionCard
          title="Emergency Contact"
          subtitle="Primary point of contact"
        >
          <div className="bg-muted/30 rounded-xl border border-border p-4">
            <div className="text-sm font-semibold text-foreground">
              {r.emergencyContact.name}
            </div>
            <div className="text-xs text-muted-foreground">
              {r.emergencyContact.relation}
            </div>
            <a
              href={`tel:${r.emergencyContact.mobile.replace(/\s/g, "")}`}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              <Phone className="h-3.5 w-3.5" /> {r.emergencyContact.mobile}
            </a>
          </div>
        </SectionCard>
      </div>

      <div className="mt-4">
        <SectionCard title="Recent Activities" subtitle="Care log entries">
          <ul className="space-y-3">
            {mockResidentActivities.map(a => (
              <li key={a.id} className="flex items-start gap-3">
                <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                <div>
                  <div className="text-sm text-foreground">{a.text}</div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    {a.when}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>
    </AdminShell>
  );
}

function Info({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}
